// --- src/components/LabelPrinter.tsx ---
import React, { useState } from 'react';
import { Printer, Trash2, X } from 'lucide-react';
import Barcode from 'react-barcode';
import { InventoryItem, SupplierProduct } from './types';
import { ConfirmDialog } from './ConfirmDialog';

interface LabelPrinterProps {
  printQueue: Array<{ item: InventoryItem; product: SupplierProduct }>;
  removeFromPrintQueue: (ean: string) => void;
  setPrintQueue: React.Dispatch<
    React.SetStateAction<Array<{ item: InventoryItem; product: SupplierProduct }>>
  >;
}

const LabelPrinter: React.FC<LabelPrinterProps> = ({
  printQueue,
  removeFromPrintQueue: _removeFromPrintQueue,
  setPrintQueue,
}) => {
  const [clearConfirm, setClearConfirm] = useState(false);
  const LABELS_PER_SHEET = 66; // 6 oszlop × 11 sor (48mm × 25mm címkék)

  const handlePrint = () => {
    window.print();
  };

  const handleClearQueue = () => {
    setClearConfirm(true);
  };

  const handleClearConfirm = () => {
    setPrintQueue([]);
    setClearConfirm(false);
  };

  // Truncate product name if too long
  const truncateName = (name: string, maxLength: number = 20) => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength - 3) + '...';
  };

  // Kiszámoljuk az aktuális ív helyeit
  const currentSheetPosition = printQueue.length % LABELS_PER_SHEET;
  const remainingOnCurrentSheet = LABELS_PER_SHEET - currentSheetPosition;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-6">
      {/* Header with Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-slate-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-gray-100 flex items-center gap-2">
              <Printer size={28} className="text-blue-600 dark:text-blue-400" />
              Címkék Nyomtatása
            </h1>
            <p className="text-slate-500 dark:text-gray-400 text-sm mt-1">
              {printQueue.length} tétel a nyomtatási sorban
              {remainingOnCurrentSheet < LABELS_PER_SHEET && printQueue.length > 0 && (
                <span className="ml-2 text-blue-600 dark:text-blue-400 font-medium">
                  ({remainingOnCurrentSheet} hely maradt az aktuális íven)
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            {printQueue.length > 0 && (
              <>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold shadow-md transition"
                >
                  <Printer size={20} />
                  Nyomtatás
                </button>
                <button
                  onClick={handleClearQueue}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold shadow-md transition"
                >
                  <Trash2 size={20} />
                  Sor Törlése
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Print Queue Grid - Optimized for A4 Label Sheets */}
      {printQueue.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-12 border border-slate-200 dark:border-gray-700 text-center">
          <p className="text-slate-400 dark:text-gray-500 text-lg">
            A nyomtatási sor üres. Adj hozzá elemeket a Készlet oldalról.
          </p>
        </div>
      ) : (
        <div className="label-sheet">
          <div className="label-grid">
            {printQueue.map(({ item, product }, index) => (
              <div key={`${item.ean}-${index}`} className="label-cell">
                {/* Remove button (hidden in print) */}
                <button
                  onClick={() => {
                    // Eltávolítjuk az adott indexű elemet
                    setPrintQueue((prev) => prev.filter((_, i) => i !== index));
                  }}
                  className="remove-btn print:hidden absolute top-1 right-1 p-1 bg-red-500 hover:bg-red-600 text-white rounded transition z-10"
                  title="Eltávolítás"
                >
                  <X size={14} />
                </button>

                {/* Label Content */}
                <div className="label-content">
                  {/* Product Name */}
                  <div className="label-name">{truncateName(product.name)}</div>

                  {/* Price */}
                  <div className="label-price">
                    {product.sellingPrice > 0
                      ? `${product.sellingPrice.toLocaleString()} Ft`
                      : `${product.netPrice.toLocaleString()} Ft`}
                  </div>
                  {/* Barcode */}
                  <div className="label-barcode">
                    <Barcode
                      value={item.ean}
                      format={item.ean.length === 13 ? 'EAN13' : 'CODE128'}
                      width={1}
                      height={30}
                      displayValue={true}
                      fontSize={9}
                      margin={0}
                      background="transparent"
                      lineColor="#000000"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          /* Hide everything except labels */
          body * {
            visibility: hidden;
          }
          
          .label-sheet,
          .label-sheet * {
            visibility: visible;
          }
          
          .label-sheet {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          
          /* Hide remove buttons in print */
          .remove-btn {
            display: none !important;
          }
          
          /* Optimize for A4 label sheets - 66 labels (6x11) 48x25mm */
          @page {
            size: A4;
            margin: 0.5cm;
          }
          
          .label-grid {
            display: grid;
            grid-template-columns: repeat(6, 32mm);
            grid-auto-rows: 25mm;
            gap: 0.1mm 1.5mm;
            page-break-inside: avoid;
          }
          
          .label-cell {
            border: none;
            padding: 0.2mm;
            page-break-inside: avoid;
            break-inside: avoid;
            overflow: hidden;
          }
          
          .label-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            height: 100%;
            font-size: 6pt;
            gap: 1mm;
          }
          
          .label-name {
            font-size: 6pt;
            font-weight: 600;
            text-align: center;
            line-height: 0.9;
            max-height: 4mm;
            overflow: hidden;
          }

          .label-price {
            font-size: 6pt;
            font-weight: 600;
          }
          
          .label-barcode {
            flex: none;
            display: flex;
            align-items: center;
            justify-content: center;
            max-height: 12mm;
          }
          
        }
        
        /* Screen Styles */
        @media screen {
          .label-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 1rem;
          }
          
          .label-cell {
            position: relative;
            background: white;
            border: 2px solid #e2e8f0;
            border-radius: 8px;
            padding: 1rem;
            min-height: 150px;
            transition: transform 0.2s, box-shadow 0.2s;
          }
          
          .label-cell:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          }
          
          .label-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            height: 100%;
            gap: 0.5rem;
          }
          
          .label-name {
            font-size: 0.875rem;
            font-weight: 600;
            text-align: center;
            color: #1e293b;
            line-height: 1.2;
            flex-shrink: 0;
          }

          .label-price {
            font-size: 1rem;
            font-weight: 700;
            color: #059669;
            text-align: center;
            flex-shrink: 0;
          }
          
          .label-barcode {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 50px;
          }
        
        }
      `}</style>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={clearConfirm}
        title="Nyomtatási sor törlése"
        message="Biztosan törölni szeretnéd az összes elemet a nyomtatási sorból?"
        variant="warning"
        confirmText="Törlés"
        cancelText="Mégse"
        onConfirm={handleClearConfirm}
        onCancel={() => setClearConfirm(false)}
      />
    </div>
  );
};

export default LabelPrinter;
